/**
 * Recommendations Page
 *
 * Shows product recommendations by use case
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Gamepad2,
  MonitorPlay,
  Briefcase,
  Palette,
  Cpu,
  TrendingUp,
  ChevronRight,
  Star
} from 'lucide-react';
import { estimateKrwPrice } from '../lib/aliexpress';
import type { AliExpressHotProduct } from '../types';

interface UseCase {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  keywords: string;
}

const USE_CASES: UseCase[] = [
  {
    id: 'gaming',
    name: '게이밍 PC',
    description: '고사양 게임을 위한 최적의 부품',
    icon: <Gamepad2 size={24} />,
    keywords: 'gaming RTX graphics card high performance'
  },
  {
    id: 'workstation',
    name: '워크스테이션',
    description: '전문 작업을 위한 고성능 PC',
    icon: <Briefcase size={24} />,
    keywords: 'workstation CPU Ryzen Threadripper professional'
  },
  {
    id: 'content',
    name: '콘텐츠 제작',
    description: '영상 편집, 3D 렌더링용',
    icon: <Palette size={24} />,
    keywords: 'video editing 3D rendering GPU memory'
  },
  {
    id: 'office',
    name: '사무용 PC',
    description: '문서 작업, 웹서핑용',
    icon: <MonitorPlay size={24} />,
    keywords: 'office desktop budget CPU integrated'
  },
];

export function RecommendationsPage() {
  const [selectedUseCase, setSelectedUseCase] = useState<string>('gaming');
  const [products, setProducts] = useState<AliExpressHotProduct[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchProducts() {
      const useCase = USE_CASES.find(u => u.id === selectedUseCase);
      if (!useCase) return;

      setLoading(true);
      try {
        const response = await fetch(`https://moapc-worker.linuxm.workers.dev/api/aliexpress/search?keywords=${encodeURIComponent(useCase.keywords)}&pageSize=12`);
        if (response.ok) {
          const data = await response.json();
          setProducts(data.products || []);
        }
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, [selectedUseCase]);

  const placeholderImage = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect fill="#f3f4f6" width="300" height="300"/><text fill="#9ca3af" font-family="Arial" font-size="14" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">Product</text></svg>')}`;

  return (
    <div className="container py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          용도별 추천 제품
        </h1>
        <p className="text-gray-600">
          사용 목적에 맞는 최적의 PC 부품을 추천해 드립니다.
        </p>
      </div>

      {/* Use Case Selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {USE_CASES.map((useCase) => (
          <button
            key={useCase.id}
            onClick={() => setSelectedUseCase(useCase.id)}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              selectedUseCase === useCase.id
                ? 'border-orange-500 bg-orange-50 text-orange-700'
                : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
              selectedUseCase === useCase.id
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 text-gray-500'
            }`}>
              {useCase.icon}
            </div>
            <h3 className="font-bold text-sm mb-1">{useCase.name}</h3>
            <p className="text-xs text-gray-500">{useCase.description}</p>
          </button>
        ))}
      </div>

      {/* Selected Use Case Info */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
            {USE_CASES.find(u => u.id === selectedUseCase)?.icon}
          </div>
          <div>
            <h2 className="text-xl font-bold">
              {USE_CASES.find(u => u.id === selectedUseCase)?.name} 추천 부품
            </h2>
            <p className="text-orange-100 text-sm">
              {USE_CASES.find(u => u.id === selectedUseCase)?.description}
            </p>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : products.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((product) => (
            <Link
              key={product.productId}
              to={`/products/aliexpress/${product.productId}`}
              state={{ product }}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-orange-300 transition-all group"
            >
              <div className="aspect-square bg-gray-100 relative overflow-hidden">
                <img
                  src={product.imageUrl || placeholderImage}
                  alt={product.productTitle}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    if (!target.src.startsWith('data:image')) {
                      target.src = placeholderImage;
                    }
                  }}
                />
                {product.originalPrice > product.salePrice && (
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                    {Math.round((1 - product.salePrice / product.originalPrice) * 100)}% OFF
                  </div>
                )}
              </div>
              <div className="p-3">
                <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2 min-h-[40px] group-hover:text-orange-600 transition-colors">
                  {product.productTitle}
                </h3>
                {product.volume > 0 && (
                  <div className="flex items-center gap-1 mb-2">
                    <TrendingUp size={12} className="text-green-500" />
                    <span className="text-xs text-gray-500">
                      {product.volume.toLocaleString()}개 판매
                    </span>
                  </div>
                )}
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-orange-600">
                    ₩{estimateKrwPrice(product.salePrice).toLocaleString()}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Cpu size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">추천 제품을 불러오는 중입니다...</p>
        </div>
      )}

      {/* CTA */}
      <div className="mt-8 text-center">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
        >
          더 많은 제품 보기
          <ChevronRight size={18} />
        </Link>
      </div>

      {/* Tips */}
      <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
        <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
          <Star size={18} className="text-yellow-500" />
          추천 팁
        </h3>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>• <strong>게이밍</strong>: GPU가 가장 중요합니다. RTX 4060 이상 추천</li>
          <li>• <strong>워크스테이션</strong>: 멀티코어 CPU와 많은 RAM이 필요합니다</li>
          <li>• <strong>콘텐츠 제작</strong>: 빠른 SSD와 CUDA 코어가 많은 GPU 추천</li>
          <li>• <strong>사무용</strong>: 내장 그래픽 CPU로 충분합니다</li>
        </ul>
      </div>
    </div>
  );
}

export default RecommendationsPage;
